import { Group, Text, Tooltip } from '@mantine/core'
// The urgency indicators come from the solid pack, not sharp-duotone: duotone draws
// a second layer at 40% opacity, which at this size washed both icons out to a pale
// smudge. Single-layer solid keeps them legible at 13px.
import { faCircleExclamation, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { TaskUrgency } from '../../utils/taskUrgency'

export interface TaskCardHeaderProps {
  type: string
  assignedTo: string
  urgency: TaskUrgency
  // Pending hold edits the user backed out of without saving. Flagged the same way the
  // Present Position row flags a kept draft, so a collapsed card never hides work.
  unsaved?: boolean
}

// Urgency indicators use distinct icon shapes as well as colour, so the state is
// never conveyed by colour alone (MSD-683 accessibility AC).
function getUrgencyIndicator(urgency: TaskUrgency) {
  if (urgency === 'overdue') return { icon: faCircleExclamation, color: 'danger.6', label: 'Overdue' }
  if (urgency === 'nearlyDue') return { icon: faTriangleExclamation, color: 'warning.6', label: 'Due soon' }
  return null
}

export function TaskCardHeader({ type, assignedTo, urgency, unsaved = false }: Readonly<TaskCardHeaderProps>) {
  const indicator = getUrgencyIndicator(urgency)

  return (
    <>
      <Group gap={6} wrap="nowrap">
        <Text fz="xs" fw={700} truncate>
          {type}
        </Text>
        {indicator && (
          <Tooltip label={indicator.label} withArrow>
            <Text span c={indicator.color} lh={1} fz={13}>
              <FontAwesomeIcon icon={indicator.icon} role="img" aria-label={indicator.label} />
            </Text>
          </Tooltip>
        )}
        {unsaved && (
          <Text fz="xs" c="var(--unsaved-text)" fs="italic" style={{ whiteSpace: 'nowrap' }}>
            Unsaved
          </Text>
        )}
      </Group>
      <Text fz="xs" c="dimmed" truncate>
        Assigned To: {assignedTo}
      </Text>
    </>
  )
}
